import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import React, { useState } from "react";
import Table from "@via/components/table/table.tsx";
import { classNames, formatDateString } from "@via/utils/helpers.tsx";
import ToggleButton from "@via/components/toggle-button.tsx";
import { useUpdateFeatureFlagsByUserId } from "@via/pages/account/admin/admin.api.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import { FeatureFlagCategoryDisplay, FeatureFlagResponse } from "common";
import { TruncatedText } from "@via/components/truncated-text.tsx";

interface FeatureFlagsTableProps {
  data: FeatureFlagResponse[];
  selectedUserId: number | undefined;
  onFlagsSave: () => Promise<void>;
}

const columnHelper = createColumnHelper<FeatureFlagResponse>();

const defaultParams = {
  pageSize: 10,
};

const FeatureFlagsTable: React.FC<FeatureFlagsTableProps> = ({
  data,
  selectedUserId,
  onFlagsSave,
}) => {
  const [changedFlags, setChangedFlags] = useState<Record<string, boolean>>({});

  /**
   * The toggle edits the USER grant, never the merged isEnabled. Organization grants joined
   * isEnabled when entitlements shipped, and a toggle seeded from it would copy every
   * organization grant into a personal grant on any save, a grant that would then outlive the
   * organization's revocation. The Source column says why a flag is on; this column edits only
   * the user's own term.
   */
  const isUserGranted = (flag: FeatureFlagResponse): boolean =>
    flag.sources.includes("user");

  const sourceLabel = (flag: FeatureFlagResponse): string => {
    const parts: string[] = [];
    if (flag.sources.includes("global")) parts.push("Global");
    if (flag.sources.includes("organization"))
      parts.push(
        `via ${flag.grantingOrganizations.map(org => org.name).join(", ")}`
      );
    if (flag.sources.includes("user")) parts.push("User");
    return parts.length > 0 ? parts.join(", ") : "—";
  };

  const {
    mutateAsync: updateFeatureFlagsByUserId,
    isPending: isGetFeatureFlagsByUserIdLoading,
  } = useUpdateFeatureFlagsByUserId();

  const onToggle = (envVarName: string, value: boolean) => {
    setChangedFlags(prev => ({
      ...prev,
      [envVarName]: value,
    }));
  };

  const saveChanges = async () => {
    const enabledFlags = data
      .filter(flag => {
        const currentValue =
          flag.envVarName in changedFlags
            ? changedFlags[flag.envVarName]
            : isUserGranted(flag);
        return currentValue;
      })
      .map(flag => flag.envVarName);

    selectedUserId &&
      (await updateFeatureFlagsByUserId({
        userId: selectedUserId,
        updatedFlags: enabledFlags,
      }));

    await onFlagsSave();
  };

  const columns = [
    columnHelper.accessor("name", {
      cell: info => <span>{info.getValue()}</span>,
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor("description", {
      cell: info => (
        <TruncatedText
          text={info.getValue()}
          maxLength={70}
          id={`tooltip-${info.column.id}-${info.row.original.name}`}
        />
      ),
      header: () => <span>Description</span>,
    }),
    columnHelper.accessor("category", {
      cell: info => (
        <TruncatedText
          text={FeatureFlagCategoryDisplay[info.getValue()]}
          id={`tooltip-${info.column.id}`}
        />
      ),
      header: () => <span>Category</span>,
    }),
    columnHelper.accessor("dateAdded", {
      cell: info => <span>{formatDateString(info.getValue(), "LLL")}</span>,
      header: () => <span>Date Added</span>,
    }),
    columnHelper.accessor("globallyOn", {
      cell: info => (info.getValue<boolean>() ? "Yes" : "No"),
      header: () => <span>Globally On</span>,
    }),
    columnHelper.accessor("sources", {
      cell: info => (
        <span title="An organization grant applies to the member everywhere, not only inside that organization.">
          {sourceLabel(info.row.original)}
        </span>
      ),
      header: () => <span>Source</span>,
      enableSorting: false,
    }),
    columnHelper.display({
      id: "userGrant",
      cell: info => {
        const flag = info.row.original;
        const isOn =
          flag.envVarName in changedFlags
            ? changedFlags[flag.envVarName]
            : isUserGranted(flag);
        return (
          <ToggleButton
            isOn={isOn}
            onToggle={() => onToggle(flag.envVarName, !isOn)}
            disabled={info.row.original.globallyOn}
          />
        );
      },
      header: () => <span>User Grant</span>,
      enableSorting: false,
    }),
  ] as Array<ColumnDef<FeatureFlagResponse>>;

  return (
    <div
      className={classNames(
        isGetFeatureFlagsByUserIdLoading ? "pointer-events-none" : "",
        "mt-5"
      )}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Feature Flags</h2>
        <button
          onClick={saveChanges}
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isGetFeatureFlagsByUserIdLoading}>
            Save Changes
          </Spinner>
        </button>
      </div>
      <Table
        data={data}
        columns={columns}
        total={data.length}
        emptyMessage="No feature flags available."
        pageSize={defaultParams.pageSize}
      />
    </div>
  );
};

export default FeatureFlagsTable;
